// Strategy ViewModel - Presentation logic for strategy selection

import { Strategy, CaseStudy } from '../model/Onboarding.types';

// Case study VM for display
export type CaseStudyVM = {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  coverImageUrl: string;
  coverImageWideUrl?: string;
  creatorName: string;
  creatorInstagramName: string;
  documentUrl: string;
};

// Strategy card VM
export type StrategyVM = {
  id: string;
  name: string;
  tagline: string | null;
  description: string | null;
  superTitle: string | null;
  pills: string[];
  features: string[];
  priceLabel: string | null;
  programLengthLabel: string | null;
  launchTimeLabel: string | null;
  coverImageUrl: string | null;
  isRecommended: boolean;
  caseStudies: CaseStudyVM[];
};

// Create CaseStudyVM from domain
// coverImageVerticalUrl is for web, coverImageHorizontalUrl is for mobile
export const createCaseStudyVM = (caseStudy: CaseStudy): CaseStudyVM => ({
  id: caseStudy.id,
  title: caseStudy.title,
  subtitle: caseStudy.subtitle,
  description: caseStudy.description,
  coverImageUrl: caseStudy.coverImageVerticalUrl,
  coverImageWideUrl: caseStudy.coverImageHorizontalUrl,
  creatorName: caseStudy.creatorName,
  creatorInstagramName: caseStudy.creatorInstagramName,
  documentUrl: caseStudy.documentUrl,
});

// Create StrategyVM from domain
export const createStrategyVM = (strategy: Strategy): StrategyVM => ({
  id: strategy.id,
  name: strategy.name,
  tagline: strategy.tagline,
  description: strategy.description,
  superTitle: strategy.superTitle,
  pills: strategy.pills,
  features: strategy.features,
  priceLabel: strategy.priceLabel,
  programLengthLabel: strategy.programLengthLabel,
  launchTimeLabel: strategy.launchTimeLabel,
  coverImageUrl: strategy.coverImageUrl,
  isRecommended: strategy.isRecommended,
  caseStudies: strategy.caseStudies.map(createCaseStudyVM),
});

// Get recommended strategy from list
export const getRecommendedStrategy = (
  strategies: StrategyVM[],
): StrategyVM | null => {
  return strategies.find((s) => s.isRecommended) ?? null;
};

// Sort strategies with recommended first
export const sortStrategiesWithRecommendedFirst = (
  strategies: StrategyVM[],
): StrategyVM[] => {
  const recommended = strategies.filter((s) => s.isRecommended);
  const others = strategies.filter((s) => !s.isRecommended);
  return [...recommended, ...others];
};

// Strategy selection screen VM
type StrategySelectionVM = {
  title: string;
  subtitle: string;
  strategies: StrategyVM[];
  recommendedStrategy: StrategyVM | null;
  caseStudies: CaseStudyVM[];
  dismissText: string;
};

// Create strategy selection screen VM
export const createStrategySelectionVM = (
  strategies: Strategy[],
): StrategySelectionVM => {
  const strategyVMs = strategies.map(createStrategyVM);
  const sortedStrategies = sortStrategiesWithRecommendedFirst(strategyVMs);
  const recommendedStrategy = getRecommendedStrategy(sortedStrategies);

  // Collect unique case studies from all strategies
  const caseStudyMap = new Map<string, CaseStudyVM>();
  for (const strategy of strategyVMs) {
    for (const cs of strategy.caseStudies) {
      if (!caseStudyMap.has(cs.id)) {
        caseStudyMap.set(cs.id, cs);
      }
    }
  }

  return {
    title: 'Your winning strategy',
    subtitle:
      'These are the best strategies, based on our experience with thousands of creators',
    strategies: sortedStrategies,
    recommendedStrategy,
    caseStudies: Array.from(caseStudyMap.values()),
    dismissText:
      "Don't feel like selecting one right now? No problem, you can choose one later.",
  };
};

// Format strategy labels for display
export const formatStrategyLabel = (
  label: string | null,
  prefix?: string,
): string => {
  if (!label) return '';
  return prefix ? `${prefix}: ${label}` : label;
};

// Get strategy emoji based on name (for visual distinction)
export const getStrategyEmoji = (strategyName: string): string => {
  const nameLower = strategyName.toLowerCase();

  if (nameLower.includes('lead') || nameLower.includes('gen')) {
    return '🎯';
  }
  // Check 'high' before 'mid/value' to correctly match "High Value"
  if (nameLower.includes('high')) {
    return '👑';
  }
  if (nameLower.includes('mid') || nameLower.includes('value')) {
    return '💎';
  }
  if (nameLower.includes('subscription') || nameLower.includes('recurring')) {
    return '🔄';
  }

  return '✨';
};
